import { useCallback } from 'react';
import { useMapStore } from '../store/map.store';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';

export const useMap = () => {
  const { mapInstance: map, zoom, center, setZoom, setCenter } = useMapStore();

  const setBasemap = useCallback(
    (type: 'road' | 'satellite' | 'hybrid' | 'terrain' | 'gray') => {
      if (!map) return;

      // Toggle visibility of basemap layers based on type
      // Hybrid = satellite imagery + label overlay
      const layers = map.getLayers();
      layers.forEach((layer: any) => {
        const name = layer.get('name');
        if (!name || !name.startsWith('base-')) return;

        if (type === 'hybrid') {
          layer.setVisible(
            name === 'base-satellite' || name === 'base-hybrid-labels',
          );
        } else {
          layer.setVisible(name === `base-${type}`);
        }
      });
    },
    [map],
  );

  const setZoomLevel = useCallback(
    (level: number) => {
      if (!map) return;
      map.getView().setZoom(level);
      setZoom(level);
    },
    [map, setZoom],
  );

  const getZoom = useCallback(() => {
    if (!map) return 0;
    return map.getView().getZoom() || 0;
  }, [map]);

  const setCenterCoordinates = useCallback(
    (lon: number, lat: number) => {
      if (!map) return;
      // Store's setCenter handles the EPSG:4326→3857 transform + view update
      setCenter([lon, lat]);
    },
    [map, setCenter],
  );

  const getCenterCoordinates = useCallback(() => {
    if (!map) return [0, 0];
    const center = map.getView().getCenter();
    if (!center) return [0, 0];
    return toLonLat(center, 'EPSG:3857') as [number, number];
  }, [map]);

  const addMarker = useCallback(
    (lon: number, lat: number) => {
      if (!map) return;
      const coordinates = fromLonLat([lon, lat], 'EPSG:3857');
      // This will be handled by a separate marker layer management system
      return coordinates;
    },
    [map],
  );

  /**
   * Fit the map view to a bounding box (EPSG:4326).
   * Nominatim returns [south, north, west, east] as strings.
   */
  const fitBoundingBox = useCallback(
    (south: number, north: number, west: number, east: number, maxZoom = 18) => {
      if (!map) return;
      // Transform from EPSG:4326 (lon/lat) to EPSG:3857 (map projection)
      const extent = transformExtent(
        [west, south, east, north],
        'EPSG:4326',
        'EPSG:3857',
      );
      map.getView().fit(extent, {
        padding: [60, 60, 60, 60],
        duration: 600,
        maxZoom,
      });
    },
    [map],
  );

  const updateSize = useCallback(() => {
    if (!map) return;
    map.updateSize();
  }, [map]);

  return {
    map,
    zoom,
    center,
    setBasemap,
    setZoom: setZoomLevel,
    getZoom,
    setCenter: setCenterCoordinates,
    getCenterCoordinates,
    fitBoundingBox,
    addMarker,
    updateSize,
  };
};
